import {
	Button,
	Grid,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useMemo, useState } from "react";
import { ErrorModal } from "../../../../../../../components/modals/error-modal";
import MesfModal from "../../../../../../../components/modals/modal.mesf";
import { ButtonWithLoading } from "../../../../../../../components/shared/buttons/button-with-loading";
import { useTrendingContext } from "../../../context/TrendingContext";
import { getViewTags } from "../../../repository/TrendingRepository";
import { useTrendingStyles } from "../../../styles/TrendingStyles";

import CreateIcon from "@material-ui/icons/Create";
import DeleteIcon from "@material-ui/icons/Delete";
import LockIcon from "@material-ui/icons/Lock";
import { isNil } from "lodash-es";
import { View, ViewTag, ViewTagsMap } from "../../../models/TrendingModels";
import { getRandomColor } from "../../../utils/tagColors";
import { DeleteViewModal } from "../deleteViewModal/DeleteViewModal";
import { EditViewModal } from "../editViewModal/EditViewModal";

interface Props {
	getTagsFromAPI: Function;
	open: boolean;
	handleClose: (shouldUpdate: boolean) => void;
}

export const LoadViewModal = ({ open, handleClose, getTagsFromAPI }: Props) => {
	const {
		state: { views, viewSelected },
		actions: { setViews, setViewTagsAndRefetch, setViewSelected },
	} = useTrendingContext();
	const classes = useTrendingStyles();
	const [viewForDelete, setViewForDelete] = useState<View | null>(null);
	const [viewForEdit, setViewForEdit] = useState<View | null>(null);

	const [deleteViewModalOpen, setDeleteViewModalOpen] = useState(false);
	const [editViewModalOpen, setEditViewModalOpen] = useState(false);

	const [allViewsChecked, setAllViewsChecked] = useState(false);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");

	const handleSelect = async () => {
		if (viewSelected) {
			setIsLoading(true);
			const viewTagsResp = await getViewTags(viewSelected.ViewId);
			if (viewTagsResp.ok) {
				setViewTagsAndRefetch(
					viewTagsResp.data.tables[0].rows.reduce(
						(carr: ViewTagsMap, curr: ViewTag, idx: number) => {
							if (isNil(curr.Color)) {
								curr.Color = getRandomColor();
							}
							carr[curr.TagId] = {
								viewTag: curr,
								order: idx,
							};
							return carr;
						},
						{},
					),
				);
			} else {
				setError(viewTagsResp.message);
			}

			setIsLoading(false);
			handleClose(false);
		}
	};

	const handleDeleteView = async (view: View, e: any) => {
		e.stopPropagation();
		if (view.ViewId === viewSelected?.ViewId) {
			setError("Can not delete current view");
		} else {
			setViewForDelete(view);
			setDeleteViewModalOpen(true);
		}
	};
	const handleEditView = async (view: View, e: any) => {
		setViewForEdit(view);
		setEditViewModalOpen(true);
	};

	const handleAllViewsCheckbox = () => {
		setAllViewsChecked((prev) => !prev);
	};

	const publicViews: View[] = useMemo(() => {
		return views.filter((v) => v.IsPublic === true);
	}, [views, allViewsChecked]);

	return (
		<div>
			<MesfModal
				open={open}
				handleClose={() => handleClose(false)}
				maxWidth={"xl"}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
				title="Views"
			>
				<MesfModal.Content>
					<Grid container>
						{views.length ? (
							<>
								<Grid item md={12}>
									<div
										className={classes.isPublicCheckbox}
										style={{ paddingBottom: "1rem" }}
									>
										<label htmlFor="">
											<input
												type="checkbox"
												checked={allViewsChecked}
												onChange={handleAllViewsCheckbox}
											/>
											<Typography>All Views</Typography>
										</label>
									</div>
								</Grid>
								<Grid item md={12}>
									<Autocomplete
										size="small"
										id="list-of-views"
										noOptionsText={
											allViewsChecked
												? "No views detected, please create a new view"
												: "No private views detected, please create a new view ."
										}
										options={allViewsChecked ? views : publicViews}
										getOptionLabel={(option) => option.ViewName}
										onChange={(ev, val) => setViewSelected(val)}
										defaultValue={viewSelected}
										style={{ width: "100%" }}
										renderOption={(option) => (
											<div className={classes.optionContainer}>
												<div className={classes.optionLabel}>
													{option.ViewName}
												</div>
												<div className={classes.iconContainer}>
													{option.IsPublic ? (
														""
													) : (
														<>
															<Tooltip title={"Private"} arrow>
																<LockIcon
																	fontSize="small"
																	style={{
																		color: "black",
																		height: "18px",
																		width: "18px",
																		cursor: "auto",
																	}}
																/>
															</Tooltip>
														</>
													)}
													<IconButton
														onClick={(
															e: React.MouseEvent<
																HTMLButtonElement,
																MouseEvent
															>,
														) => handleEditView(option, e)}
													>
														<CreateIcon
															fontSize="small"
															color="primary"
															style={{
																height: "18px",
																width: "18px",
																cursor: "pointer",
															}}
														/>
													</IconButton>
													<IconButton
														onClick={(
															e: React.MouseEvent<
																HTMLButtonElement,
																MouseEvent
															>,
														) => handleDeleteView(option, e)}
													>
														<DeleteIcon
															fontSize="small"
															color="secondary"
															style={{
																height: "18px",
																width: "18px",
																cursor: "pointer",
															}}
														/>
													</IconButton>
												</div>
											</div>
										)}
										renderInput={(params) => (
											<TextField {...params} label="Views" variant="outlined" />
										)}
									/>
								</Grid>
							</>
						) : (
							<Typography>No views detected, please add one</Typography>
						)}
					</Grid>
				</MesfModal.Content>
				<MesfModal.Actions>
					<div style={{ paddingTop: "1rem" }}>
						<Button
							variant="outlined"
							style={{ marginRight: "1rem" }}
							color="secondary"
							onClick={() => handleClose(false)}
						>
							Cancel
						</Button>
						{views.length ? (
							<ButtonWithLoading
								onClick={handleSelect}
								variant={"contained"}
								color={"primary"}
								isLoading={isLoading}
								style={{ marginRight: "1rem" }}
							>
								Apply
							</ButtonWithLoading>
						) : (
							<Button
								variant="contained"
								style={{ marginRight: "1rem" }}
								color="primary"
								onClick={() => handleClose(false)}
							>
								Ok
							</Button>
						)}
					</div>
				</MesfModal.Actions>
			</MesfModal>
			<DeleteViewModal
				open={deleteViewModalOpen}
				view={viewForDelete}
				handleClose={(shouldUpdate: boolean) => {
					setDeleteViewModalOpen(false);
					if (shouldUpdate) getTagsFromAPI().then(() => {});
				}}
			/>
			<EditViewModal
				open={editViewModalOpen}
				view={viewForEdit}
				handleClose={(shouldUpdate: boolean) => {
					setEditViewModalOpen(false);
					if (shouldUpdate) getTagsFromAPI().then(() => {});
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</div>
	);
};
